import { comparableText } from "../../boundaries/text-search.search";
import type { GovernanceState } from "../../enums/govern";
import { GOVERNANCE_STATE } from "../../enums/govern";
import type { DeliveryBrief } from "../brief/briefTypes";
import { isAssigned } from "./governStatus";
import type { GovernCheck, GovernWorkItem } from "./governTypes";

export function createGovernWork(
  websiteId: string,
  brief: DeliveryBrief,
): GovernWorkItem {
  return {
    id: `${websiteId}:${brief.id}`,
    websiteId,
    sourceBriefId: brief.id,
    sourceType: brief.sourceType,
    sourceSnapshot: brief,
    sourceFingerprint: briefFingerprint(brief),
    state: GOVERNANCE_STATE.READY_FOR_DELIVERY,
    roles: {
      deliveryOwner: assignedValue(brief.owner),
      reviewer: "",
      approver: "",
      verifier: "",
      reviewOwner: "",
    },
    checks: checksFromBrief(brief),
    supersededChecks: [],
    deliveryNotes: "",
    reworkReason: "",
    reworkOwner: "",
    verifiedAt: null,
    reviewSchedule: null,
    openedAt: Date.now(),
  };
}

export function reconcileGovernWork(
  work: GovernWorkItem,
  brief: DeliveryBrief | null,
): GovernWorkItem {
  if (!brief) return work;
  const nextChecks = checksFromBrief(brief);
  const existing = new Map(
    work.checks.map((check) => [check.criterionKey, check]),
  );
  const reconciled = nextChecks.map((check) => {
    const previous = existing.get(check.criterionKey);
    return previous?.criterionText === check.criterionText ? previous : check;
  });
  const currentKeys = new Set(reconciled.map((check) => check.criterionKey));
  const newlySuperseded = work.checks.filter(
    (check) => !currentKeys.has(check.criterionKey),
  );
  const criteriaChanged =
    reconciled.length !== work.checks.length ||
    reconciled.some((check, index) => check !== work.checks[index]);
  const nextFingerprint = briefFingerprint(brief);
  const sourceChanged = work.sourceFingerprint !== nextFingerprint;
  if (!criteriaChanged && !sourceChanged) return work;

  return {
    ...work,
    sourceSnapshot: brief,
    sourceFingerprint: nextFingerprint,
    checks: reconciled,
    supersededChecks: uniqueChecks([
      ...work.supersededChecks,
      ...newlySuperseded,
    ]),
    state:
      sourceChanged && isCompletedState(work.state)
        ? GOVERNANCE_STATE.READY_FOR_REVIEW
        : work.state,
    verifiedAt: sourceChanged ? null : work.verifiedAt,
  };
}

function criterionKey(briefId: string, criterion: string): string {
  return `${briefId}:${stableHash(normalizeCriterion(criterion))}`;
}

function checksFromBrief(brief: DeliveryBrief): GovernCheck[] {
  return brief.acceptanceCriteria.map((criterion) => ({
    id: criterionKey(brief.id, criterion),
    criterionKey: criterionKey(brief.id, criterion),
    criterionText: criterion.trim(),
    required: true,
    result: "not_checked",
    evidenceNote: "",
    evidenceLink: "",
    verifiedBy: "",
    verificationSource: "",
  }));
}

function normalizeCriterion(value: string): string {
  return comparableText(value);
}

function stableHash(value: string): string {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function uniqueChecks(checks: GovernCheck[]): GovernCheck[] {
  return [...new Map(checks.map((check) => [check.id, check])).values()];
}

function assignedValue(value: string): string {
  return isAssigned(value) ? value.trim() : "";
}

function isCompletedState(state: GovernanceState): boolean {
  return (
    state === GOVERNANCE_STATE.VERIFIED ||
    state === GOVERNANCE_STATE.REVIEW_SCHEDULED
  );
}

function briefFingerprint(brief: DeliveryBrief): string {
  return stableHash(
    JSON.stringify({
      title: brief.title,
      objective: brief.objective,
      requiredChange: brief.requiredChange,
      scopeNotes: brief.scopeNotes,
      outOfScopeNotes: brief.outOfScopeNotes,
      requirements: brief.requirements,
      acceptanceCriteria: brief.acceptanceCriteria,
      openQuestions: brief.openQuestions,
      owner: brief.owner,
      status: brief.status,
      pages: brief.pages.map((page) => ({ id: page.id, path: page.path })),
    }),
  );
}
